import type {
  CampaignTemplateQueryResponse,
  CampaignTemplateQueryVariables,
} from '~/api/graphql/queries/campaignTemplate.generated';
import type {
  CampaignGroupQueryResponse,
  CampaignGroupQueryVariables,
} from '~/api/graphql/queries/campaignGroup.generated';
import type { ICampaignGroup, ICampaignGroupTemplate } from '~/modules/campaign-group/types';
import type { ICampaignTemplate } from '~/modules/campaign-template/types';
import type { Campaign, Variant } from '~/modules/campaign/types';

import {
  CampaignTemplateDocument,
  useCampaignTemplateQuery,
} from '~/api/graphql/queries/campaignTemplate.generated';
import {
  CampaignGroupDocument,
  useCampaignGroupQuery,
} from '~/api/graphql/queries/campaignGroup.generated';
import { useVariantTemplateUpdateMutation } from '~/api/graphql/mutations/variantTemplateUpdate.generated';
import { useVariantPriceDeleteMutation } from '~/api/graphql/mutations/variantPriceDelete.generated';
import { CAMPAIGN_MSG, ICampaignType, useUpdateDeleteCampaignMutation } from '~/modules/campaign';
import { useVariantUrlDeleteMutation } from '~/api/graphql/mutations/variantUrlDelete.generated';
import { useVariantUpdateMutation } from '~/api/graphql/mutations/variantUpdate.generated';
import { mappingDataCampaignGroup } from '~/modules/campaign-group/hooks';
import { useCampaignGroupStore } from '~/modules/campaign-group/stores';
import { useCampaignStore } from '~/modules/campaign/stores';
import { captureSentryError } from '~/exceptions/sentry';
import { appFetcher } from '~/api/utils/gatewayClient';
import { useQueryClient } from '@tanstack/react-query';
import { clearTemplateFromCampaign } from '../utils';
import { useToast } from '~/hooks/useToast';
import { useState } from 'react';

export interface IRemoveTemplateFromCampaignParams {
  campaignId: string;
  campaignType: ICampaignType;
  /** Required for TEMPLATE and TEMPLATE_BUNDLE. */
  templateKey?: string;
  /** Required for PRICE. */
  priceId?: string;
  /** Required for URL. */
  urlId?: string;
  /**
   * Read the campaign from the API instead of the campaign stores. Those stores are loaded once
   * per session, so a caller whose UI comes from another source (the pick-page modal reads
   * `campaignVariantTargets`) would otherwise offer a removal the stale store cannot carry out.
   */
  shouldFetchLatest?: boolean;
}

export interface IRemoveTemplateFromCampaignResult {
  /** Resolves false when the removal did not go through, so callers can keep their modal open. */
  handleRemoveTemplate: (params: IRemoveTemplateFromCampaignParams) => Promise<boolean>;
  isLoading: boolean;
}

/**
 * One kind of variant record being removed, told apart by the accessors rather than by a type flag:
 * every kind lives on its own field of the variant and behind its own delete mutation.
 */
interface ICascadingVariantRecordsRemoval {
  /** Undefined when the campaign is already gone, which leaves nothing to cascade to or patch. */
  campaign?: Campaign;
  campaignId: string;
  /** Names the kind in the error a failed deletion throws. */
  campaignType: ICampaignType;
  /** The record the removal was asked for. */
  recordId: string;
  toRecordId: (variant?: Variant | null) => string | undefined;
  deleteRecord: (id: string) => Promise<unknown>;
  /** The variant with its record dropped, for the store patch. */
  toClearedVariant: (variant: Variant) => Variant;
}

const CLEAR_VARIANT_INPUT = {
  templateSuffix: '',
  templateType: '',
  themePageID: '0',
};

const CLEAR_VARIANT_TEMPLATE_INPUT = {
  redirectURL: '',
  themeID: '',
  title: undefined,
  thumbnailURL: undefined,
};

export const useRemoveTemplateFromCampaign = (): IRemoveTemplateFromCampaignResult => {
  const { shopifyToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);

  const queryClient = useQueryClient();

  const getCampaignById = useCampaignStore((state) => state.getCampaignById);
  const upsertCampaign = useCampaignStore((state) => state.upsertCampaign);
  const findCampaignGroupById = useCampaignGroupStore((state) => state.findCampaignGroupById);
  const upsertCampaignGroup = useCampaignGroupStore((state) => state.upsertCampaignGroup);

  const { mutateAsync: variantUpdate } = useVariantUpdateMutation();
  const { mutateAsync: variantTemplateUpdate } = useVariantTemplateUpdateMutation();
  const { mutateAsync: campaignDelete } = useUpdateDeleteCampaignMutation();
  const { mutateAsync: variantPriceDelete } = useVariantPriceDeleteMutation();
  const { mutateAsync: variantUrlDelete } = useVariantUrlDeleteMutation();

  const clearVariantTemplate = async (variant: Variant): Promise<Variant> => {
    const variantTask = variantUpdate({
      id: variant.id,
      input: { ...CLEAR_VARIANT_INPUT, trafficRate: variant.trafficRate },
    });

    const templateTask = variant.template?.id
      ? variantTemplateUpdate({ id: variant.template.id, input: CLEAR_VARIANT_TEMPLATE_INPUT })
      : Promise.resolve(null);

    const [variantRes, templateRes] = await Promise.all([variantTask, templateTask]);

    return {
      ...variant,
      ...variantRes.variantUpdate,
      ...(templateRes?.variantTemplateUpdate
        ? { template: { ...variant.template, ...templateRes.variantTemplateUpdate } }
        : {}),
    };
  };

  // The 7-day global staleTime would hand back whatever this tab cached, so both reads force a
  // network round trip -- the point of fetching is to see what the other tabs have done.
  const fetchLatestCampaign = async (campaignId: string): Promise<Campaign | undefined> => {
    const data = await queryClient.fetchQuery(
      useCampaignTemplateQuery.getKey({ id: campaignId }),
      appFetcher<CampaignTemplateQueryResponse, CampaignTemplateQueryVariables>(
        CampaignTemplateDocument,
        {
          id: campaignId,
        },
      ),
      { staleTime: 0 },
    );

    // `campaign(id)` still answers for a soft-deleted campaign, which is nothing left to remove from.
    if (!data.campaign || data.campaign.deletedAt) return undefined;

    return data.campaign as Campaign;
  };

  const fetchLatestCampaignGroup = async (groupId: string): Promise<ICampaignGroup | undefined> => {
    const data = await queryClient.fetchQuery(
      useCampaignGroupQuery.getKey({ id: groupId }),
      appFetcher<CampaignGroupQueryResponse, CampaignGroupQueryVariables>(CampaignGroupDocument, {
        id: groupId,
      }),
      { staleTime: 0 },
    );

    return data.campaignGroup
      ? mappingDataCampaignGroup(data.campaignGroup as ICampaignGroup)
      : undefined;
  };

  /**
   * Undefined means the campaign is already gone, which only a fetched read can tell us. A store
   * miss says nothing about the server, so it throws rather than passing for a completed removal.
   */
  const resolveCampaign = async (
    campaignId: string,
    shouldFetchLatest?: boolean,
  ): Promise<Campaign | undefined> => {
    if (shouldFetchLatest) return fetchLatestCampaign(campaignId);

    const campaign = getCampaignById(campaignId);
    if (!campaign) throw new Error(`Campaign ${campaignId} not found in store`);

    return campaign;
  };

  const resolveCampaignGroup = async (
    groupId: string,
    shouldFetchLatest?: boolean,
  ): Promise<ICampaignGroup | undefined> => {
    if (shouldFetchLatest) return fetchLatestCampaignGroup(groupId);

    const group = findCampaignGroupById(groupId);
    if (!group) throw new Error(`Campaign group ${groupId} not found in store`);

    return group;
  };

  const removeFromSingleCampaign = async (
    campaignId: string,
    templateKey: string,
    shouldFetchLatest?: boolean,
  ) => {
    const campaign = await resolveCampaign(campaignId, shouldFetchLatest);
    if (!campaign) return;

    const updatedCampaign = await clearTemplateFromCampaign(
      campaign,
      templateKey,
      clearVariantTemplate,
    );
    upsertCampaign({ ...campaign, variants: updatedCampaign.variants });
  };

  const removeFromCampaignGroup = async (
    groupId: string,
    templateKey: string,
    shouldFetchLatest?: boolean,
  ) => {
    const group = await resolveCampaignGroup(groupId, shouldFetchLatest);
    if (!group) return;

    const groupCampaigns = (group.campaigns || []) as ICampaignTemplate[];

    // In a multipage experiment each campaign is a (Control, Variant) pair for one URL.
    // Removing a pair's Control template makes the pair invalid, so soft-delete the
    // whole campaign. Variant-only removal keeps the pair and clears just that side.
    const updatedCampaigns = await Promise.all(
      groupCampaigns.map(async (campaign): Promise<ICampaignTemplate> => {
        const matched =
          campaign.variants?.filter((v): v is Variant => v?.templateSuffix === templateKey) ?? [];
        if (!matched.length) return campaign;

        if (matched.some((v) => v.isOrigin)) {
          const response = await campaignDelete({ id: campaign.id });
          return { ...campaign, deletedAt: response?.campaignUpdate?.deletedAt };
        }

        return clearTemplateFromCampaign(campaign, templateKey, clearVariantTemplate);
      }),
    );

    upsertCampaignGroup({ ...group, campaigns: updatedCampaigns } as ICampaignGroupTemplate);
  };

  /**
   * Deletes the record the removal names, plus every record of the same kind when that one belongs
   * to Control: the Variant side is only meaningful measured against Control, so a pair that lost it
   * would be invalid. Removing a challenger's record keeps Control intact.
   *
   * Unlike the multipage path above, the emptied campaign is not soft-deleted: an experiment with no
   * set-up variant is a Draft state that renders, so the merchant picks again rather than losing the
   * experiment. A multipage pair has no such state -- its Control template is what defines the pair --
   * which is why that path deletes.
   *
   * Reading the campaign is best-effort: a caller working off fresher data than this session holds
   * still gets its own record deleted, there is just no pair to cascade to or store to patch.
   */
  const removeCascadingVariantRecords = async ({
    campaign,
    campaignId,
    campaignType,
    recordId,
    toRecordId,
    deleteRecord,
    toClearedVariant,
  }: ICascadingVariantRecordsRemoval) => {
    const variants = campaign?.variants ?? [];

    const isControlRemoved = variants.some(
      (variant) => toRecordId(variant) === recordId && variant?.isOrigin,
    );
    const recordIds = isControlRemoved
      ? [
          ...new Set(
            variants.flatMap((variant) => {
              const id = toRecordId(variant);

              return id ? [id] : [];
            }),
          ),
        ]
      : [recordId];

    const results = await Promise.allSettled(recordIds.map((id) => deleteRecord(id)));

    // Patch the store by what actually landed. Failing the whole cascade would otherwise leave it
    // claiming a deleted record is still there, and the retry would run against an id the API has
    // already dropped -- stuck on exactly the invalid pair the cascade exists to prevent.
    const deletedIds = new Set(
      recordIds.filter((_, index) => results[index].status === 'fulfilled'),
    );

    if (campaign?.variants && deletedIds.size) {
      const newVariants = campaign.variants.map((variant) => {
        const recordIdOfVariant = toRecordId(variant);

        return variant && recordIdOfVariant && deletedIds.has(recordIdOfVariant)
          ? toClearedVariant(variant)
          : variant;
      });
      upsertCampaign({ ...campaign, variants: newVariants });
    }

    const rejected = results.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    );
    if (rejected.length) {
      throw new AggregateError(
        rejected.map((result) => result.reason),
        `${campaignType} campaign ${campaignId}: ${rejected.length}/${recordIds.length} deletions failed`,
      );
    }
  };

  /**
   * A price experiment's Variant side runs on a duplicate of the Control's product, and that
   * duplicate inherits the same template — so removing the Control's price leaves the duplicate
   * still holding the template, which is what makes the cascade necessary here.
   */
  const removeFromPriceCampaign = async (
    campaignId: string,
    priceId: string,
    shouldFetchLatest?: boolean,
  ) => {
    const campaign = shouldFetchLatest
      ? await fetchLatestCampaign(campaignId)
      : getCampaignById(campaignId);

    await removeCascadingVariantRecords({
      campaign,
      campaignId,
      campaignType: ICampaignType.PRICE,
      recordId: priceId,
      toRecordId: (variant) => variant?.price?.id,
      deleteRecord: (id) => variantPriceDelete({ id }),
      toClearedVariant: (variant) => ({ ...variant, price: undefined }),
    });
  };

  /**
   * A URL experiment sends each side to a page the merchant already has, so removing one hands that
   * page back untouched — there is no duplicate to clean up. Control still takes the whole pair with
   * it, since a variant URL with nothing to be measured against is not a test.
   */
  const removeFromUrlCampaign = async (
    campaignId: string,
    urlId: string,
    shouldFetchLatest?: boolean,
  ) => {
    const campaign = shouldFetchLatest
      ? await fetchLatestCampaign(campaignId)
      : getCampaignById(campaignId);

    await removeCascadingVariantRecords({
      campaign,
      campaignId,
      campaignType: ICampaignType.URL,
      recordId: urlId,
      toRecordId: (variant) => variant?.url?.id,
      deleteRecord: (id) => variantUrlDelete({ id }),
      toClearedVariant: (variant) => ({ ...variant, url: undefined }),
    });
  };

  const handleRemoveTemplate = async ({
    campaignId,
    campaignType,
    templateKey,
    priceId,
    urlId,
    shouldFetchLatest,
  }: IRemoveTemplateFromCampaignParams): Promise<boolean> => {
    setIsLoading(true);
    try {
      if (campaignType === ICampaignType.PRICE) {
        if (!priceId)
          throw new Error(`Price campaign ${campaignId} removal is missing its price id`);

        await removeFromPriceCampaign(campaignId, priceId, shouldFetchLatest);
        return true;
      }

      if (campaignType === ICampaignType.URL) {
        if (!urlId) throw new Error(`URL campaign ${campaignId} removal is missing its URL id`);

        await removeFromUrlCampaign(campaignId, urlId, shouldFetchLatest);
        return true;
      }

      if (!templateKey)
        throw new Error(`Campaign ${campaignId} removal is missing its template key`);

      if (campaignType === ICampaignType.TEMPLATE) {
        await removeFromSingleCampaign(campaignId, templateKey, shouldFetchLatest);
        return true;
      }

      await removeFromCampaignGroup(campaignId, templateKey, shouldFetchLatest);
      return true;
    } catch (error) {
      captureSentryError(error as Error, {
        source: 'useRemoveTemplateFromCampaign.handleRemoveTemplate',
        level: 'warning',
        tags: { experiment: 'Experiment' },
      });
      shopifyToast(CAMPAIGN_MSG.REMOVE_TEMPLATE.FAILED, { isError: true });

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { handleRemoveTemplate, isLoading };
};
